import { useState, useEffect } from "react";
import { useForm } from "react-hook-form";
import { zodResolver } from "@hookform/resolvers/zod";
import { z } from "zod";
import {
  Dialog,
  DialogContent,
  DialogDescription,
  DialogFooter,
  DialogHeader,
  DialogTitle,
} from "@/components/ui/dialog";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";
import { Textarea } from "@/components/ui/textarea";
import {
  Select,
  SelectContent,
  SelectItem,
  SelectTrigger,
  SelectValue,
} from "@/components/ui/select";
import {
  Form,
  FormControl,
  FormField,
  FormItem,
  FormLabel,
  FormMessage,
} from "@/components/ui/form";
import { useToast } from "@/hooks/use-toast";
import { recruitmentAPI } from "@/lib/api";
import { Upload, FileText, X, Download } from "lucide-react";

const candidatureSchema = z.object({
  offre_id: z.string().min(1, "L'offre d'emploi est requise"),
  nom: z
    .string()
    .min(1, "Le nom est requis")
    .max(100, "Le nom ne peut pas dépasser 100 caractères"),
  prenom: z
    .string()
    .min(1, "Le prénom est requis")
    .max(100, "Le prénom ne peut pas dépasser 100 caractères"),
  email: z.string().email("Email invalide"),
  telephone: z
    .string()
    .optional()
    .refine(
      (value) => {
        if (!value) return true; // Optional field
        // Moroccan phone number validation
        // Accepts formats like: +212 6XXXXXXXX, +212 7XXXXXXXX, +212 5XXXXXXXX
        // Also accepts local formats: 06XXXXXXXX, 07XXXXXXXX, 05XXXXXXXX
        const moroccanPhoneRegex = /^(\+212\s?[567]\d{8}|0[567]\d{8})$/;
        return moroccanPhoneRegex.test(value.replace(/\s/g, ""));
      },
      {
        message:
          "Numéro de téléphone marocain invalide. Format accepté: +212 6XXXXXXXX, +212 7XXXXXXXX, +212 5XXXXXXXX",
      }
    ),
  lettre_motivation: z.string().optional(),
  statut: z
    .enum(["recue", "preselectionne", "entretien", "acceptee", "refusee"])
    .optional(),
  note_evaluation: z
    .union([z.string(), z.number()])
    .optional()
    .transform((val) => {
      if (val === "" || val === undefined || val === null) return undefined;
      const num = typeof val === "string" ? parseInt(val) : val;
      return isNaN(num) ? undefined : num;
    })
    .refine((val) => val === undefined || (val >= 0 && val <= 20), {
      message: "La note doit être entre 0 et 20",
    }),
  commentaires: z.string().optional(),
});

const CandidatureForm = ({
  isOpen,
  onClose,
  candidature = null,
  jobOffers = [],
  onSuccess,
}) => {
  const [loading, setLoading] = useState(false);
  const [cvFile, setCvFile] = useState(null);
  const [motivationFile, setMotivationFile] = useState(null);
  const [cvPreview, setCvPreview] = useState(null);
  const [motivationPreview, setMotivationPreview] = useState(null);
  const { toast } = useToast();

  const form = useForm({
    resolver: zodResolver(candidatureSchema),
    defaultValues: {
      offre_id: "",
      nom: "",
      prenom: "",
      email: "",
      telephone: "",
      lettre_motivation: "",
      statut: "recue",
      note_evaluation: "",
      commentaires: "",
    },
  });

  const isEdit = !!candidature;

  useEffect(() => {
    if (isOpen) {
      console.log("Job offers available:", jobOffers);
      if (candidature) {
        // Edit mode - populate form with existing data
        form.reset({
          offre_id: candidature.offre_id || "",
          nom: candidature.nom || "",
          prenom: candidature.prenom || "",
          email: candidature.email || "",
          telephone: candidature.telephone || "",
          lettre_motivation: candidature.lettre_motivation || "",
          statut: candidature.statut || "recue",
          note_evaluation: candidature.note_evaluation || "",
          commentaires: candidature.commentaires || "",
        });

        // Set file previews if files exist
        if (candidature.has_cv_file) {
          setCvPreview({
            name: candidature.cv_file_name || "CV.pdf",
            size: candidature.cv_file_size,
            type: candidature.cv_file_type,
          });
        }
        if (candidature.has_motivation_file) {
          setMotivationPreview({
            name: candidature.motivation_file_name || "Lettre_Motivation.pdf",
            size: candidature.motivation_file_size,
            type: candidature.motivation_file_type,
          });
        }
      } else {
        // Create mode - reset form with first available job offer
        const defaultOffreId = jobOffers.length > 0 ? jobOffers[0].id : "";
        console.log("Setting default offre_id:", defaultOffreId);
        form.reset({
          offre_id: defaultOffreId,
          nom: "",
          prenom: "",
          email: "",
          telephone: "",
          lettre_motivation: "",
          statut: "recue",
          note_evaluation: "",
          commentaires: "",
        });
        setCvFile(null);
        setMotivationFile(null);
        setCvPreview(null);
        setMotivationPreview(null);
      }
    }
  }, [isOpen, candidature, form, jobOffers]);

  const handleFileChange = (file, type) => {
    if (!file) return;

    const maxSize = 5 * 1024 * 1024; // 5MB
    if (file.size > maxSize) {
      toast({
        title: "Erreur",
        description: "Le fichier ne peut pas dépasser 5MB",
        variant: "destructive",
      });
      return;
    }

    if (type === "cv") {
      setCvFile(file);
      setCvPreview({
        name: file.name,
        size: file.size,
        type: file.type,
      });
    } else if (type === "motivation") {
      setMotivationFile(file);
      setMotivationPreview({
        name: file.name,
        size: file.size,
        type: file.type,
      });
    }
  };

  const removeFile = (type) => {
    if (type === "cv") {
      setCvFile(null);
      setCvPreview(null);
    } else if (type === "motivation") {
      setMotivationFile(null);
      setMotivationPreview(null);
    }
  };

  const convertFileToBase64 = (file) => {
    return new Promise((resolve, reject) => {
      const reader = new FileReader();
      reader.readAsDataURL(file);
      reader.onload = () => {
        const base64 = reader.result.split(",")[1]; // Remove data:type;base64, prefix
        resolve(base64);
      };
      reader.onerror = (error) => reject(error);
    });
  };

  const onSubmit = async (data) => {
    try {
      setLoading(true);
      console.log("Form data:", data);
      console.log("Is edit mode:", isEdit);
      console.log("Available job offers:", jobOffers);

      const formData = { ...data };

      // Convert files to base64 if provided
      if (cvFile) {
        const cvBase64 = await convertFileToBase64(cvFile);
        formData.cv_file_data = cvBase64;
        formData.cv_file_name = cvFile.name;
        formData.cv_file_type = cvFile.type;
        formData.cv_file_size = cvFile.size;
      }

      if (motivationFile) {
        const motivationBase64 = await convertFileToBase64(motivationFile);
        formData.motivation_file_data = motivationBase64;
        formData.motivation_file_name = motivationFile.name;
        formData.motivation_file_type = motivationFile.type;
        formData.motivation_file_size = motivationFile.size;
      }

      if (isEdit) {
        // For edit mode, remove offre_id from the data as it shouldn't be updated
        delete formData.offre_id;
        console.log("Updating application with data:", formData);
        await recruitmentAPI.updateApplication(candidature.id, formData);
        toast({
          title: "Succès",
          description: "Candidature mise à jour avec succès",
        });
      } else {
        // For new candidatures, ensure we have an offre_id
        if (!formData.offre_id) {
          toast({
            title: "Erreur",
            description: "Veuillez sélectionner une offre d'emploi",
            variant: "destructive",
          });
          return;
        }
        if (jobOffers.length === 0) {
          toast({
            title: "Erreur",
            description:
              "Aucune offre d'emploi disponible. Veuillez créer une offre d'abord.",
            variant: "destructive",
          });
          return;
        }
        console.log("Creating application with data:", formData);
        await recruitmentAPI.submitApplication(formData);
        toast({
          title: "Succès",
          description: "Candidature créée avec succès",
        });
      }

      onSuccess?.();
      onClose();
    } catch (error) {
      console.error("Error saving candidature:", error);
      toast({
        title: "Erreur",
        description:
          error.response?.data?.error ||
          error.response?.data?.message ||
          "Erreur lors de la sauvegarde",
        variant: "destructive",
      });
    } finally {
      setLoading(false);
    }
  };

  const downloadFile = async (fileType) => {
    try {
      const response = await recruitmentAPI.downloadApplicationFile(
        candidature.id,
        fileType
      );

      // Create blob and download
      const blob = new Blob([response.data]);
      const url = window.URL.createObjectURL(blob);
      const link = document.createElement("a");
      link.href = url;

      const fileName =
        fileType === "cv"
          ? candidature.cv_file_name ||
            `CV_${candidature.prenom}_${candidature.nom}.pdf`
          : candidature.motivation_file_name ||
            `Lettre_Motivation_${candidature.prenom}_${candidature.nom}.pdf`;

      link.download = fileName;
      document.body.appendChild(link);
      link.click();
      document.body.removeChild(link);
      window.URL.revokeObjectURL(url);
    } catch (error) {
      console.error("Error downloading file:", error);
      toast({
        title: "Erreur",
        description: "Impossible de télécharger le fichier",
        variant: "destructive",
      });
    }
  };

  const formatFileSize = (bytes) => {
    if (bytes === 0) return "0 Bytes";
    const k = 1024;
    const sizes = ["Bytes", "KB", "MB", "GB"];
    const i = Math.floor(Math.log(bytes) / Math.log(k));
    return parseFloat((bytes / Math.pow(k, i)).toFixed(2)) + " " + sizes[i];
  };

  return (
    <Dialog open={isOpen} onOpenChange={onClose}>
      <DialogContent className="max-w-2xl max-h-[90vh] overflow-y-auto">
        <DialogHeader>
          <DialogTitle>
            {isEdit ? "Modifier la candidature" : "Nouvelle candidature"}
          </DialogTitle>
          <DialogDescription>
            {isEdit
              ? "Modifiez les informations de la candidature"
              : "Créez une nouvelle candidature"}
          </DialogDescription>
        </DialogHeader>

        <Form {...form}>
          <form
            onSubmit={form.handleSubmit(onSubmit, (errors) => {
              console.log("Form validation errors:", errors);
              toast({
                title: "Erreur de validation",
                description: "Veuillez corriger les erreurs dans le formulaire",
                variant: "destructive",
              });
            })}
            className="space-y-6"
          >
            <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
              <FormField
                control={form.control}
                name="prenom"
                render={({ field }) => (
                  <FormItem>
                    <FormLabel>Prénom *</FormLabel>
                    <FormControl>
                      <Input placeholder="Prénom" {...field} />
                    </FormControl>
                    <FormMessage />
                  </FormItem>
                )}
              />

              <FormField
                control={form.control}
                name="nom"
                render={({ field }) => (
                  <FormItem>
                    <FormLabel>Nom *</FormLabel>
                    <FormControl>
                      <Input placeholder="Nom" {...field} />
                    </FormControl>
                    <FormMessage />
                  </FormItem>
                )}
              />
            </div>

            <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
              <FormField
                control={form.control}
                name="email"
                render={({ field }) => (
                  <FormItem>
                    <FormLabel>Email *</FormLabel>
                    <FormControl>
                      <Input
                        type="email"
                        placeholder="email@example.com"
                        {...field}
                      />
                    </FormControl>
                    <FormMessage />
                  </FormItem>
                )}
              />

              <FormField
                control={form.control}
                name="telephone"
                render={({ field }) => (
                  <FormItem>
                    <FormLabel>Téléphone</FormLabel>
                    <FormControl>
                      <Input placeholder="+212 6 12 34 56 78" {...field} />
                    </FormControl>
                    <FormMessage />
                  </FormItem>
                )}
              />
            </div>

            {/* Job Offer Selection - only for create mode */}
            {!isEdit && (
              <FormField
                control={form.control}
                name="offre_id"
                render={({ field }) => (
                  <FormItem>
                    <FormLabel>Offre d'emploi *</FormLabel>
                    <Select
                      onValueChange={field.onChange}
                      defaultValue={field.value}
                    >
                      <FormControl>
                        <SelectTrigger>
                          <SelectValue placeholder="Choisir une offre d'emploi" />
                        </SelectTrigger>
                      </FormControl>
                      <SelectContent>
                        {jobOffers.length > 0 ? (
                          jobOffers.map((job) => (
                            <SelectItem key={job.id} value={job.id}>
                              {job.titre} - {job.departement?.nom}
                            </SelectItem>
                          ))
                        ) : (
                          <SelectItem value="" disabled>
                            Aucune offre d'emploi disponible
                          </SelectItem>
                        )}
                      </SelectContent>
                    </Select>
                    <FormMessage />
                    {jobOffers.length === 0 && (
                      <p className="text-sm text-red-600">
                        Aucune offre d'emploi disponible. Veuillez créer une
                        offre d'emploi d'abord.
                      </p>
                    )}
                  </FormItem>
                )}
              />
            )}

            <FormField
              control={form.control}
              name="lettre_motivation"
              render={({ field }) => (
                <FormItem>
                  <FormLabel>Lettre de motivation (texte)</FormLabel>
                  <FormControl>
                    <Textarea
                      placeholder="Écrivez votre lettre de motivation..."
                      rows={4}
                      {...field}
                    />
                  </FormControl>
                  <FormMessage />
                </FormItem>
              )}
            />

            {/* File Upload Sections */}
            <div className="space-y-4">
              <div>
                <Label className="text-sm font-medium">
                  CV (PDF, DOC, DOCX)
                </Label>
                <div className="mt-2">
                  {cvPreview ? (
                    <div className="flex items-center justify-between p-3 border rounded-lg bg-gray-50">
                      <div className="flex items-center gap-2">
                        <FileText className="h-4 w-4 text-blue-600" />
                        <div>
                          <p className="text-sm font-medium">
                            {cvPreview.name}
                          </p>
                          <p className="text-xs text-gray-500">
                            {formatFileSize(cvPreview.size)}
                          </p>
                        </div>
                      </div>
                      <div className="flex items-center gap-2">
                        {isEdit && candidature.has_cv_file && (
                          <Button
                            type="button"
                            variant="outline"
                            size="sm"
                            onClick={() => downloadFile("cv")}
                          >
                            <Download className="h-4 w-4" />
                          </Button>
                        )}
                        <Button
                          type="button"
                          variant="outline"
                          size="sm"
                          onClick={() => removeFile("cv")}
                        >
                          <X className="h-4 w-4" />
                        </Button>
                      </div>
                    </div>
                  ) : (
                    <div className="border-2 border-dashed border-gray-300 rounded-lg p-6 text-center">
                      <Upload className="h-8 w-8 text-gray-400 mx-auto mb-2" />
                      <p className="text-sm text-gray-600 mb-2">
                        Cliquez pour télécharger un CV
                      </p>
                      <input
                        type="file"
                        accept=".pdf,.doc,.docx"
                        onChange={(e) =>
                          handleFileChange(e.target.files[0], "cv")
                        }
                        className="hidden"
                        id="cv-upload"
                      />
                      <Button
                        type="button"
                        variant="outline"
                        size="sm"
                        onClick={() =>
                          document.getElementById("cv-upload").click()
                        }
                      >
                        Choisir un fichier
                      </Button>
                    </div>
                  )}
                </div>
              </div>

              <div>
                <Label className="text-sm font-medium">
                  Lettre de motivation (PDF, DOC, DOCX)
                </Label>
                <div className="mt-2">
                  {motivationPreview ? (
                    <div className="flex items-center justify-between p-3 border rounded-lg bg-gray-50">
                      <div className="flex items-center gap-2">
                        <FileText className="h-4 w-4 text-green-600" />
                        <div>
                          <p className="text-sm font-medium">
                            {motivationPreview.name}
                          </p>
                          <p className="text-xs text-gray-500">
                            {formatFileSize(motivationPreview.size)}
                          </p>
                        </div>
                      </div>
                      <div className="flex items-center gap-2">
                        {isEdit && candidature.has_motivation_file && (
                          <Button
                            type="button"
                            variant="outline"
                            size="sm"
                            onClick={() => downloadFile("motivation")}
                          >
                            <Download className="h-4 w-4" />
                          </Button>
                        )}
                        <Button
                          type="button"
                          variant="outline"
                          size="sm"
                          onClick={() => removeFile("motivation")}
                        >
                          <X className="h-4 w-4" />
                        </Button>
                      </div>
                    </div>
                  ) : (
                    <div className="border-2 border-dashed border-gray-300 rounded-lg p-6 text-center">
                      <Upload className="h-8 w-8 text-gray-400 mx-auto mb-2" />
                      <p className="text-sm text-gray-600 mb-2">
                        Cliquez pour télécharger une lettre de motivation
                      </p>
                      <input
                        type="file"
                        accept=".pdf,.doc,.docx"
                        onChange={(e) =>
                          handleFileChange(e.target.files[0], "motivation")
                        }
                        className="hidden"
                        id="motivation-upload"
                      />
                      <Button
                        type="button"
                        variant="outline"
                        size="sm"
                        onClick={() =>
                          document.getElementById("motivation-upload").click()
                        }
                      >
                        Choisir un fichier
                      </Button>
                    </div>
                  )}
                </div>
              </div>
            </div>

            {/* Evaluation fields (only for edit mode) */}
            {isEdit && (
              <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
                <FormField
                  control={form.control}
                  name="statut"
                  render={({ field }) => (
                    <FormItem>
                      <FormLabel>Statut</FormLabel>
                      <Select
                        onValueChange={field.onChange}
                        defaultValue={field.value}
                      >
                        <FormControl>
                          <SelectTrigger>
                            <SelectValue placeholder="Choisir un statut" />
                          </SelectTrigger>
                        </FormControl>
                        <SelectContent>
                          <SelectItem value="recue">Reçue</SelectItem>
                          <SelectItem value="preselectionne">
                            Présélectionné
                          </SelectItem>
                          <SelectItem value="entretien">Entretien</SelectItem>
                          <SelectItem value="acceptee">Acceptée</SelectItem>
                          <SelectItem value="refusee">Refusée</SelectItem>
                        </SelectContent>
                      </Select>
                      <FormMessage />
                    </FormItem>
                  )}
                />

                <FormField
                  control={form.control}
                  name="note_evaluation"
                  render={({ field }) => (
                    <FormItem>
                      <FormLabel>Note d'évaluation (0-20)</FormLabel>
                      <FormControl>
                        <Input
                          type="number"
                          min="0"
                          max="20"
                          placeholder="Ex: 15"
                          {...field}
                          value={field.value || ""}
                        />
                      </FormControl>
                      <FormMessage />
                    </FormItem>
                  )}
                />
              </div>
            )}

            <FormField
              control={form.control}
              name="commentaires"
              render={({ field }) => (
                <FormItem>
                  <FormLabel>Commentaires</FormLabel>
                  <FormControl>
                    <Textarea
                      placeholder="Ajoutez vos commentaires..."
                      rows={3}
                      {...field}
                    />
                  </FormControl>
                  <FormMessage />
                </FormItem>
              )}
            />

            <DialogFooter>
              <Button type="button" variant="outline" onClick={onClose}>
                Annuler
              </Button>
              <Button type="submit" disabled={loading}>
                {loading
                  ? "Enregistrement..."
                  : isEdit
                  ? "Mettre à jour"
                  : "Créer"}
              </Button>
            </DialogFooter>
          </form>
        </Form>
      </DialogContent>
    </Dialog>
  );
};

export default CandidatureForm;
